import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

// HTML lessons (s134). The instructor's page is rendered through `srcdoc` in a
// sandbox WITHOUT allow-same-origin: its scripts run in an opaque origin, so they
// can never read the student's session token (localStorage) or call the API as
// them. A tiny reporter script posts the document height back so the frame can
// grow with its content ("Fit to content"); we only trust messages whose source
// is this very iframe.
const SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-modals';

// Measures the CONTENT (body's bottom edge + its margin), not scrollHeight:
// scrollHeight never drops below the frame's own height, so it can only grow.
const REPORTER = '<script>(function(){var last=0;function send(){var b=document.body;if(!b)return;'
  + 'var cs=getComputedStyle(b);var h=Math.ceil(b.getBoundingClientRect().bottom+(window.scrollY||0)'
  + '+(parseFloat(cs.marginBottom)||0));'
  + 'if(h>0&&h!==last){last=h;parent.postMessage({lmsHtmlHeight:h},"*");}}'
  + 'addEventListener("load",send);if(window.ResizeObserver){new ResizeObserver(send).observe(document.body||document.documentElement);}'
  + 'setInterval(send,1200);send();})();</script>';

function withReporter(html) {
  const src = html || '';
  const i = src.search(/<\/body\s*>/i);
  return i === -1 ? src + REPORTER : src.slice(0, i) + REPORTER + src.slice(i);
}

const HtmlFrame = forwardRef(function HtmlFrame({ html, title, height = 'auto', fixedPx = 720, minPx = 240, className = '' }, outerRef) {
  const frameRef = useRef(null);
  const [autoPx, setAutoPx] = useState(480);
  const srcDoc = useMemo(() => withReporter(html), [html]);

  useEffect(() => {
    if (height !== 'auto') return undefined;
    const onMsg = (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      const h = Number(e.data && e.data.lmsHtmlHeight);
      if (Number.isFinite(h) && h > 0) setAutoPx(Math.min(20000, Math.max(minPx, h)));
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [height, minPx]);

  return (
    <div ref={outerRef} className={className}>
      <iframe
        ref={frameRef}
        title={title}
        sandbox={SANDBOX}
        referrerPolicy="no-referrer"
        srcDoc={srcDoc}
        className="block w-full border-0 bg-white"
        style={{ height: height === 'auto' ? autoPx : fixedPx }}
      />
    </div>
  );
});

export default HtmlFrame;
